import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Button, Dropdown } from '@teo/components';
import { useTranslation } from 'react-i18next';
import { clamp, newUid, uploadFile as uploadFileSelect } from '@/util';
import TimeControl from '@/components/TimeControl';
import { ArrowLeft, ArrowRight, Trash } from '@teo/components/icons';
import { SplitIcon } from '@/components/Froala/widgets/ChatGPT/Icon/index';
import { SubtitleTimeline } from './SubtitleTimeline';

const MIN_MS = 200;

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeCue = (cue = {}, index = 0) => {
  const start_ms = Math.max(0, Math.round(toNumber(cue.start_ms, 0)));
  const end_ms = Math.max(
    start_ms + MIN_MS,
    Math.round(toNumber(cue.end_ms, start_ms + 2000))
  );

  return {
    id: cue.id || `subtitle-${newUid(8)}-${index}`,
    start_ms,
    end_ms,
    text: String(cue.text || ''),
  };
};

const normalizeCues = (cues = []) =>
  (Array.isArray(cues) ? cues : [])
    .map((cue, index) => normalizeCue(cue, index))
    .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0));

const msToMMSS = (ms = 0) => {
  const totalSec = Math.max(0, Math.round((ms || 0) / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const parseTimecodeToMs = (raw) => {
  const value = String(raw || '')
    .trim()
    .replace(',', '.');
  const parts = value.split(':');
  if (parts.length < 2 || parts.length > 3) return NaN;

  const seconds = Number(parts.pop());
  const minutes = Number(parts.pop());
  const hours = parts.length ? Number(parts.pop()) : 0;

  if (![hours, minutes, seconds].every((n) => Number.isFinite(n))) return NaN;
  return Math.round(((hours * 60 + minutes) * 60 + seconds) * 1000);
};

const formatVttTime = (ms = 0) => {
  const total = Math.max(0, Math.round(ms));
  const hours = Math.floor(total / 3600000);
  const minutes = Math.floor((total % 3600000) / 60000);
  const seconds = Math.floor((total % 60000) / 1000);
  const millis = total % 1000;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
};

const formatSrtTime = (ms = 0) => formatVttTime(ms).replace('.', ',');

const parseSubtitleBlocks = (text = '') => {
  const normalized = String(text || '')
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .trim();
  if (!normalized) return [];

  const blocks = normalized.split(/\n{2,}/);
  const cues = [];

  blocks.forEach((block) => {
    const lines = block.split('\n').map((line) => line.replace(/\s+$/, ''));
    if (!lines.length) return;

    const head = String(lines[0] || '').trim();
    if (!head) return;
    if (/^WEBVTT/i.test(head)) return;
    if (/^(NOTE|STYLE|REGION)\b/i.test(head)) return;

    let timingIndex = 0;
    if (/^\d+$/.test(head) && lines[1]?.includes('-->')) timingIndex = 1;
    else if (!head.includes('-->') && lines[1]?.includes('-->'))
      timingIndex = 1;

    const timingLine = lines[timingIndex];
    if (!timingLine || !timingLine.includes('-->')) return;

    const [startTokenRaw, endRaw = ''] = timingLine.split(/\s+-->\s+/);
    const endTokenRaw = endRaw.split(/\s+/)[0];

    const start_ms = parseTimecodeToMs(startTokenRaw);
    const end_ms = parseTimecodeToMs(endTokenRaw);
    if (!Number.isFinite(start_ms) || !Number.isFinite(end_ms)) return;

    const textLines = lines.slice(timingIndex + 1);
    cues.push(
      normalizeCue(
        {
          start_ms,
          end_ms,
          text: textLines.join('\n').trim(),
        },
        cues.length
      )
    );
  });

  return normalizeCues(cues);
};

export const parseVtt = (text = '') => parseSubtitleBlocks(text);
export const parseSrt = (text = '') => parseSubtitleBlocks(text);

export const subtitlesToVtt = (cues = []) => {
  const lines = ['WEBVTT', ''];

  normalizeCues(cues).forEach((cue) => {
    lines.push(
      `${formatVttTime(cue.start_ms)} --> ${formatVttTime(cue.end_ms)}`
    );
    lines.push(String(cue.text || ''));
    lines.push('');
  });

  return `${lines.join('\n').trim()}\n`;
};

export const subtitlesToSrt = (cues = []) => {
  const lines = [];

  normalizeCues(cues).forEach((cue, index) => {
    lines.push(String(index + 1));
    lines.push(
      `${formatSrtTime(cue.start_ms)} --> ${formatSrtTime(cue.end_ms)}`
    );
    lines.push(String(cue.text || ''));
    lines.push('');
  });

  return `${lines.join('\n').trim()}\n`;
};

const detectFormatFromText = (text = '') =>
  /^\s*WEBVTT/i.test(text) ? 'vtt' : 'srt';

const detectFormatFromUrl = (url = '') =>
  String(url).toLowerCase().includes('.srt') ? 'srt' : 'vtt';

const downloadTextFile = (filename, text, mimeType) => {
  if (typeof document === 'undefined') return;
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const findSubtitleTrackElement = (videoEl, preferredLanguage) => {
  if (!videoEl?.querySelectorAll) return null;

  const tracks = Array.from(
    videoEl.querySelectorAll('track[kind="subtitles"], track[kind="captions"]')
  );
  if (!tracks.length) return null;

  const preferred = String(preferredLanguage || '').toLowerCase();
  const exact = tracks.find(
    (track) =>
      String(track.getAttribute('srclang') || '').toLowerCase() === preferred
  );
  return (
    exact || tracks.find((track) => track.hasAttribute('default')) || tracks[0]
  );
};

const makeEmptySubtitle = () => ({
  id: `subtitle-${newUid(8)}`,
  start_ms: 0,
  end_ms: 2000,
  text: '',
});

export const SubtitleEditor = forwardRef(function SubtitleEditor(
  {
    value,
    onChange,
    durationSec = 0,
    currentTime = 0,
    onSeek,
    activeSubtitleIndex,
    setActiveSubtitleIndex,
    isPlaying = false,
    videoRef,
    trackSrc,
    trackLanguage,
    title,
    className = '',
    emptyLabel,
    showTimeline: controlledShowTimeline,
    hideTimelineToggle = false,
    hideHeader = false,
    hideEmptyGenerateAction = false,
    hideMeta = false,
    autoLoadTrack = true,

    onGenerateWithAi,
    isGeneratingWithAi = false,
    generateWithAiLabel,
    generatingWithAiLabel,
  },
  ref
) {
  const { t } = useTranslation();
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState(() =>
    normalizeCues(value || [])
  );
  const [internalActiveIndex, setInternalActiveIndex] = useState(null);
  const [loadState, setLoadState] = useState({ status: 'idle', error: '' });
  const [internalShowTimeline, setInternalShowTimeline] = useState(
    controlledShowTimeline !== undefined ? controlledShowTimeline : true
  );

  useEffect(() => {
    if (controlledShowTimeline !== undefined) {
      setInternalShowTimeline(controlledShowTimeline);
    }
  }, [controlledShowTimeline]);

  const showTimeline =
    controlledShowTimeline !== undefined
      ? controlledShowTimeline
      : internalShowTimeline;

  const subtitles = useMemo(
    () => normalizeCues(isControlled ? value : internalValue),
    [isControlled, value, internalValue]
  );

  useEffect(() => {
    if (!isControlled) return;
    setInternalValue(normalizeCues(value || []));
  }, [isControlled, value]);

  const setSubtitles = useCallback(
    (nextOrUpdater) => {
      const next =
        typeof nextOrUpdater === 'function'
          ? normalizeCues(nextOrUpdater(subtitles))
          : normalizeCues(nextOrUpdater);

      if (!isControlled) setInternalValue(next);
      onChange?.(next);
      return next;
    },
    [isControlled, onChange, subtitles]
  );

  const selectedIndex = activeSubtitleIndex ?? internalActiveIndex;
  const setSelectedIndex = setActiveSubtitleIndex || setInternalActiveIndex;

  const loadText = useCallback(
    (text, formatHint) => {
      const format = formatHint || detectFormatFromText(text);
      const next = format === 'srt' ? parseSrt(text) : parseVtt(text);
      setSubtitles(next);
      setLoadState({ status: 'loaded', error: '' });
      return next;
    },
    [setSubtitles]
  );

  const loadFromUrl = useCallback(
    async (url, options = {}) => {
      if (!url) {
        throw new Error(
          t('pages.edit_subtitle.errors.missing_subtitle_url', {
            defaultValue: 'Missing subtitle URL',
          })
        );
      }
      setLoadState({ status: 'loading', error: '' });

      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        throw new Error(
          t('pages.edit_subtitle.errors.subtitle_track_request_failed', {
            defaultValue: 'Subtitle track request failed with {{status}}',
            status: res.status,
          })
        );
      }

      const text = await res.text();
      return loadText(text, options.format || detectFormatFromUrl(url));
    },
    [loadText, t]
  );

  const loadFromVideo = useCallback(async () => {
    const videoEl = videoRef?.current;
    const trackEl = findSubtitleTrackElement(videoEl, trackLanguage);
    const src = trackEl?.getAttribute('src') || trackSrc;
    if (!src) {
      throw new Error(
        t('pages.edit_subtitle.errors.no_subtitle_track_found', {
          defaultValue: 'No subtitle track found on the video element',
        })
      );
    }
    return loadFromUrl(src, { format: detectFormatFromUrl(src) });
  }, [loadFromUrl, t, trackLanguage, trackSrc, videoRef]);

  const exportAsVtt = useCallback(() => subtitlesToVtt(subtitles), [subtitles]);
  const exportAsSrt = useCallback(() => subtitlesToSrt(subtitles), [subtitles]);

  useImperativeHandle(
    ref,
    () => ({
      loadVttText: (text) => loadText(text, 'vtt'),
      loadSrtText: (text) => loadText(text, 'srt'),
      loadFromUrl,
      loadFromVideo,
      exportVtt: exportAsVtt,
      exportSrt: exportAsSrt,
      getSubtitles: () => subtitles,
    }),
    [exportAsSrt, exportAsVtt, loadFromUrl, loadFromVideo, loadText, subtitles]
  );

  useEffect(() => {
    if (!autoLoadTrack) return;
    if (subtitles.length) return;
    if (!trackSrc && !videoRef?.current) return;

    let cancelled = false;

    (async () => {
      try {
        await (trackSrc
          ? loadFromUrl(trackSrc, { format: detectFormatFromUrl(trackSrc) })
          : loadFromVideo());

        if (cancelled) return;
      } catch (error) {
        if (cancelled) return;
        setLoadState({
          status: 'error',
          error:
            error?.message ||
            t('pages.edit_subtitle.errors.failed_to_load_subtitle_track', {
              defaultValue: 'Failed to load subtitle track',
            }),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [autoLoadTrack, loadFromUrl, loadFromVideo, subtitles.length, trackSrc, videoRef, t]);

  const updateSubtitle = (index, patch) => {
    setSubtitles((prev) => {
      const next = prev.slice();
      next[index] = normalizeCue({ ...next[index], ...patch }, index);
      return next;
    });
  };

  const addBefore = (index) => {
    let seekStart = 0;
    let seekEnd = 0;

    setSubtitles((prev) => {
      const next = prev.slice();
      const cur = next[index];
      const prevEnd =
        index > 0
          ? toNumber(next[index - 1].end_ms, 0)
          : Math.max(0, toNumber(cur?.start_ms, 0) - 2000);
      const start_ms = Math.max(0, prevEnd);
      const end_ms = Math.max(
        start_ms + MIN_MS,
        toNumber(cur?.start_ms, start_ms + 2000)
      );
      seekStart = start_ms;
      seekEnd = end_ms;
      next.splice(
        index,
        0,
        normalizeCue({ ...makeEmptySubtitle(), start_ms, end_ms }, index)
      );
      return next;
    });

    setSelectedIndex(index + 1);
    onSeek?.(
      Math.min(durationSec || seekEnd / 1000 || 0, (seekStart + 50) / 1000)
    );
  };

  const addAfter = (index) => {
    let seekStart = 0;
    let seekEnd = 0;

    setSubtitles((prev) => {
      const next = prev.slice();
      const cur = next[index];
      const start_ms = Math.max(0, toNumber(cur?.end_ms, 0));
      const nextStart =
        index < next.length - 1
          ? toNumber(next[index + 1].start_ms, start_ms + 2000)
          : start_ms + 2000;
      const end_ms = Math.max(start_ms + MIN_MS, nextStart);
      seekStart = start_ms;
      seekEnd = end_ms;
      next.splice(
        index + 1,
        0,
        normalizeCue({ ...makeEmptySubtitle(), start_ms, end_ms }, index + 1)
      );
      return next;
    });

    setSelectedIndex(index + 2);
    onSeek?.(
      Math.min(durationSec || seekEnd / 1000 || 0, (seekStart + 50) / 1000)
    );
  };

  const splitSubtitle = (index) => {
    let seekStart = 0;
    let seekEnd = 0;

    setSubtitles((prev) => {
      const next = prev.slice();
      const cur = next[index];
      const mid = Math.max(
        0,
        Math.round(
          (toNumber(cur?.end_ms, 0) - toNumber(cur?.start_ms, 0)) / 2 +
            toNumber(cur?.start_ms, 0)
        )
      );
      const end_ms = Math.max(mid + MIN_MS, toNumber(cur?.end_ms, mid + 2000));
      const leftText = String(cur?.text || '').trim();
      const words = leftText.split(/\s+/).filter(Boolean);
      const splitAt = words.length > 1 ? Math.ceil(words.length / 2) : 1;

      next[index] = normalizeCue(
        {
          ...cur,
          end_ms: mid,
          text: words.slice(0, splitAt).join(' ') || leftText,
        },
        index
      );

      next.splice(
        index + 1,
        0,
        normalizeCue(
          {
            ...makeEmptySubtitle(),
            start_ms: mid,
            end_ms,
            text: words.slice(splitAt).join(' '),
          },
          index + 1
        )
      );

      seekStart = mid;
      seekEnd = end_ms;
      return next;
    });

    setSelectedIndex(index + 2);
    onSeek?.(
      Math.min(durationSec || seekEnd / 1000 || 0, (seekStart + 50) / 1000)
    );
  };

  const removeSubtitle = (index) => {
    setSubtitles((prev) => {
      const next = prev.slice();
      next.splice(index, 1);
      return next;
    });
    setSelectedIndex(null);
  };

  const onImportClick = async (format) => {
    const files = await uploadFileSelect(
      false,
      format === 'srt' ? '.srt,text/plain' : '.vtt,text/vtt'
    );
    const file = files?.[0];
    if (!file) return;

    const text = await file.text();
    loadText(text, format);
  };

  const effectiveDurationSec = Math.max(
    durationSec || 0,
    ...subtitles.map((cue) => Math.max(0, toNumber(cue.end_ms, 0) / 1000))
  );

  const selectSubtitle = useCallback(
    (index, startMs, shouldSeek = true) => {
      setSelectedIndex(index + 1);

      if (shouldSeek) {
        onSeek?.(clamp((startMs + 50) / 1000, 0, effectiveDurationSec || 0));
      }
    },
    [effectiveDurationSec, onSeek, setSelectedIndex]
  );

  const handleEmptyGenerateClick = () => {
    if (onGenerateWithAi) {
      onGenerateWithAi();
      return;
    }

    const next = normalizeCue(
      {
        ...makeEmptySubtitle(),
        end_ms: Math.max(
          MIN_MS,
          Math.round(
            (durationSec || 2) * 1000 > 5000 ? 2000 : (durationSec || 2) * 1000
          )
        ),
      },
      0
    );

    setSubtitles([next]);
    setSelectedIndex(1);
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`.trim()}>
      {!hideHeader && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-grey-01 px-4 py-3">
          <div>
            <div className="text-base font-semibold text-black">
              {title ||
                t('pages.edit_subtitle.subtitle_list', {
                  defaultValue: 'Subtitles',
                })}
            </div>
            <div className="text-sm text-grey-06">
              {subtitles.length}{' '}
              {subtitles.length === 1
                ? t('pages.edit_subtitle.cue', { defaultValue: 'cue' })
                : t('pages.edit_subtitle.cues', { defaultValue: 'cues' })}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => onImportClick('vtt')}>
              {t('pages.edit_subtitle.import_vtt', {
                defaultValue: 'Import .vtt',
              })}
            </Button>
            <Button variant="outline" onClick={() => onImportClick('srt')}>
              {t('pages.edit_subtitle.import_srt', {
                defaultValue: 'Import .srt',
              })}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                downloadTextFile(
                  'subtitles.vtt',
                  exportAsVtt(),
                  'text/vtt;charset=utf-8'
                )
              }
              disabled={!subtitles.length}
            >
              {t('pages.edit_subtitle.export_vtt', {
                defaultValue: 'Export .vtt',
              })}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                downloadTextFile(
                  'subtitles.srt',
                  exportAsSrt(),
                  'application/x-subrip;charset=utf-8'
                )
              }
              disabled={!subtitles.length}
            >
              {t('pages.edit_subtitle.export_srt', {
                defaultValue: 'Export .srt',
              })}
            </Button>
          </div>
        </div>
      )}

      {loadState.status === 'error' && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {loadState.error}
        </div>
      )}

      {!hideMeta && (
        <div className="text-xs text-grey-04">
          {t('pages.create_edit_course.video_instruction.steps', {
            defaultValue: 'Steps',
          })}
          : {subtitles.length} ·{' '}
          {msToMMSS(Math.round(effectiveDurationSec * 1000))}
        </div>
      )}

      {showTimeline && (
        <SubtitleTimeline
          durationSec={effectiveDurationSec}
          subtitles={subtitles}
          currentTime={currentTime}
          onSeek={onSeek}
          onUpdateSubtitle={(i, start_ms, end_ms) =>
            updateSubtitle(i, {
              start_ms,
              end_ms,
            })
          }
          onUpdateMany={(updates) =>
            updates.forEach(({ _origIndex, patch }) =>
              updateSubtitle(_origIndex, patch)
            )
          }
          activeSubtitleIndex={selectedIndex}
          setActiveSubtitleIndex={setSelectedIndex}
          isPlaying={isPlaying}
        />
      )}

      {subtitles.length ? (
        <div className="flex flex-col gap-2">
          {subtitles.map((cue, index) => {
            const isActive = selectedIndex === index + 1;
            const startMs = cue.start_ms ?? 0;
            const endMs = cue.end_ms ?? startMs;
            const durMs = Math.max(0, endMs - startMs);

            return (
              <div
                key={cue.id || index}
                className={`rounded-lg border bg-white px-4 py-4 ${
                  isActive ? 'border-secondary-04' : 'border-grey-01'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  selectSubtitle(index, startMs, true);
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-3 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-grey-02 bg-grey-01 text-sm font-semibold text-grey-08">
                        {index + 1}
                      </div>
                      <div className="text-xs text-grey-05">
                        {msToMMSS(startMs)} – {msToMMSS(endMs)} •{' '}
                        {msToMMSS(durMs)}
                      </div>
                    </div>

                    <textarea
                      value={cue.text || ''}
                      onFocus={(e) => {
                        e.stopPropagation();
                        selectSubtitle(index, startMs, false);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectSubtitle(index, startMs, false);
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        selectSubtitle(index, startMs, false);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyUp={(e) => {
                        e.stopPropagation();
                      }}
                      onInput={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        updateSubtitle(index, { text: e.target.value });
                      }}
                      className="w-full resize-none border-0 bg-transparent p-0 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                      placeholder={
                        emptyLabel ||
                        t('pages.edit_subtitle.subtitle_text_placeholder', {
                          defaultValue: 'Subtitle text',
                        })
                      }
                    />

                    {isActive && (
                      <div className="-mt-[1px] flex border-t pt-3">
                        <TimeControl
                          start={startMs}
                          end={endMs}
                          onChange={(newTime) =>
                            updateSubtitle(index, {
                              start_ms: newTime?.start,
                              end_ms: newTime?.end,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  <div
                    className={`my-auto ml-auto flex flex-col gap-2 ${
                      isActive ? 'visible' : 'invisible'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown>
                      <Dropdown.Trigger>
                        <Button
                          onClick={async () => {}}
                          variant="ghost"
                          className="flex text-grey-01"
                        >
                          <img
                            src="/images/icons/copy.svg"
                            alt={t('pages.edit_subtitle.subtitle_actions', {
                              defaultValue: 'Subtitle actions',
                            })}
                            className="w-5"
                          />
                        </Button>
                      </Dropdown.Trigger>

                      <Dropdown.Content
                        style={{
                          top: '-8px',
                          right: '48px',
                        }}
                      >
                        <Dropdown.Actions>
                          <Dropdown.Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addBefore(index);
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <ArrowLeft className="h-5 w-5" />
                              {t('pages.edit_subtitle.add_before', {
                                defaultValue: 'Add before',
                              })}
                            </div>
                          </Dropdown.Button>
                          <Dropdown.Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addAfter(index);
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <ArrowRight className="h-5 w-5" />
                              {t('pages.edit_subtitle.add_after', {
                                defaultValue: 'Add after',
                              })}
                            </div>
                          </Dropdown.Button>
                          <Dropdown.Button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              splitSubtitle(index);
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              <SplitIcon className="h-5 w-5" />
                              {t('pages.edit_subtitle.split', {
                                defaultValue: 'Split',
                              })}
                            </div>
                          </Dropdown.Button>
                        </Dropdown.Actions>
                      </Dropdown.Content>
                    </Dropdown>

                    <Button
                      Icon={Trash}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSubtitle(index);
                      }}
                      variant="ghost"
                      className="flex text-grey-01"
                    >
                      <img
                        src="/images/icons/trash_icon_red.svg"
                        alt={t('pages.edit_subtitle.delete_subtitle', {
                          defaultValue: 'Delete subtitle',
                        })}
                        className="w-5"
                      />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-grey-03 bg-white p-6 text-sm text-grey-06">
          {emptyLabel ||
            t('pages.edit_subtitle.no_subtitles', {
              defaultValue: 'No subtitles loaded yet.',
            })}
          {!hideEmptyGenerateAction && (
            <div className="mt-4">
              <Button
                variant="outline"
                disabled={isGeneratingWithAi}
                onClick={handleEmptyGenerateClick}
              >
                {isGeneratingWithAi
                  ? generatingWithAiLabel ||
                    t('pages.edit_subtitle.generating_with_ai', {
                      defaultValue: 'Generating...',
                    })
                  : generateWithAiLabel ||
                    t('pages.edit_subtitle.generate_subtitles_with_ai', {
                      defaultValue: 'Generate subtitles with AI',
                    })}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
